import * as angular from 'angular';
import * as _ from 'lodash';
import * as helpers from '../../common/helpers';

import { IWidget, IdataWidget, ISeriesConfiguration, IWidgetFactory } from '../../widgets';

import {
    IValueViewModel,
    Group,
    StringReplacement,
    Dashboard,
    DashboardConfiguration
} from '../../models';

import {
    IIdentifierVmService,
    IGroupService,
    ILoggerService,
    ISeriesSelectorService,
    IWidgetSerializationService
} from '../../services';
import { Helper } from '../../common/helpers';
import { ValueTypes } from '../../common/ValueTypes';
import { specialWidgetTypes } from '../../states/group/validation/validation.constants';

export interface WidgetMappingResult {
    widget: { guid: string; name: string; };
    mappings: MappingResult;
}

export interface MappingResult {
    irreplacible: helpers.VmMapping[];
    mapping: { [originalSeriesGuid: string]: IValueViewModel };
}

export interface WidgetWithMappings {
    copy: IWidget;
    mappings: MappingResult
}

export interface DashboardWithMapping {
    copy: any;
    mappingResults: WidgetMappingResult[];
}

export interface IwidgetService {
    /**
     * Clones a widget by retrieving series according to specified replacements.
     * @param widget The original widget to clone.
     * @param newName The name of the new diget.
     * @param seriesReplacement The list of replacement in identifier or tag names.
     * @param groupsReplacement The list of replacements in group unique name.
     */
    cloneWidget(widget: any, newName: string, seriesReplacement: StringReplacement[],
        groupsReplacement: StringReplacement[]): angular.IPromise<{ copy: any; mappings: MappingResult; }>;

    cloneDashboard(dashboard: any, newName: string, seriesReplacement: StringReplacement[],
        groupsReplacement: StringReplacement[]): angular.IPromise<{ copy: any, mappingResults: WidgetMappingResult[] }>;
    /**
     * Returns the list of series of a widget accodring to its type.
     * @param widget The widget to get the series from.
     */
    getSeries(widget: IWidget): ISeriesConfiguration[];
}

export class WidgetService implements IwidgetService {
    static serviceId = 'widgetService';

    static $inject = [
        '$q',
        'identifierVmService',
        'groupService',
        'loggerService',
        'seriesSelectorService',
        'widgetSerializationService',
        'widgetFactory'
    ];

    constructor(
        private _$q: angular.IQService,
        private _identifierVmService: IIdentifierVmService,
        private _groupService: IGroupService,
        private _loggerService: ILoggerService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _widgetSerializationService: IWidgetSerializationService,
        private _widgetFactory: IWidgetFactory
    ) {
    }

    cloneWidget(
        widget: IWidget,
        newName: string,
        seriesReplacement: StringReplacement[],
        groupsReplacement: StringReplacement[]
    ): angular.IPromise<WidgetWithMappings> {
        return specialWidgetTypes.indexOf(widget.type) >= 0
            ? this._$q.when({ copy: this.getForSpecialWidgetTypes(widget), mappings: { irreplacible: [], mapping: {} } } as WidgetWithMappings)
            : this._$q.all([
                    this._groupService.getAll(),
                    this._identifierVmService.getAllViewModels()
                ])
                .then(responses => {
                    const groups = responses[0].map(dto => dto.group);
                    const vms = responses[1];
                    return this.getMapped(widget, newName, vms, groups, seriesReplacement, groupsReplacement);
                });
    }

    private getMapped(widget: IWidget, newName: string, vms: IValueViewModel[], groups: Group[], seriesReplacement: StringReplacement[], groupsReplacement: StringReplacement[]) {
        const series = this.getSeries(widget);
        const seriesGuids = series ? series.map(d => d.guid) : [];
        const data = this.remapSeries(seriesGuids, vms, groups, seriesReplacement, groupsReplacement);
        const mapped = this.map(widget, newName, data, series, true);
        return mapped;
    }

    cloneDashboard(
            dashboard: any,
            newName: string,
            seriesReplacement: StringReplacement[],
            groupsReplacement: StringReplacement[]
        ): angular.IPromise<DashboardWithMapping> {
        return this._$q.all([this._groupService.getAll(), this._identifierVmService.getAllViewModels()])
            .then(responses=>{
                const groups = responses[0].map(dto => dto.group);
                const vms = responses[1];
                return this.cloneWidgets(dashboard, newName, vms, groups, seriesReplacement, groupsReplacement);
            });
    }

    private cloneWidgets(
            dashboard: Dashboard,
            newName: string,
            vms: IValueViewModel[],
            groups: Group[],
            seriesReplacement: StringReplacement[],
            groupsReplacement: StringReplacement[]
        ): DashboardWithMapping {
        const copy: Dashboard = this.createCopy(dashboard);
        copy.name = newName;
        copy.configuration.widgets = [];
        const widgetsWithMapping = dashboard.configuration.widgets
        .map((widget: IWidget) => {
            const typed =
            typeof widget.configuration === typeof('')
                ? this._widgetSerializationService.createTypedWidget(widget)
                : widget;
            return specialWidgetTypes.indexOf(typed.type) >= 0
                ? {copy:this.getForSpecialWidgetTypes(typed), mappings: { irreplacible: [], mapping: {} } }
                : this.getMapped(typed, typed.name, vms, groups, seriesReplacement, groupsReplacement);
        });
        copy.configuration.widgets = widgetsWithMapping.map(wwm => this._widgetSerializationService.serializeWidget(wwm.copy));
        const results: WidgetMappingResult[] = widgetsWithMapping.map(d=>({ mappings: d.mappings, widget:{guid:d.copy.guid, name: d.copy.name}}));
        return { copy, mappingResults: results };
    }

    private getForSpecialWidgetTypes(widget: IWidget) {
        const copy = this._widgetFactory.createWidget(widget.type);
        copy.configuration = angular.copy(widget.configuration);
        copy.sizeX = widget.sizeX;
        copy.sizeY = widget.sizeY;
        copy.isTitleHidden = widget.isTitleHidden;
        return copy;
    }

    private createCopy(dashboard: Dashboard): any {
        const copy: Dashboard & { data: any } = {} as Dashboard & { data: any };
        for (const key in dashboard) {
            if (!key.startsWith('$') && !key.startsWith('_')) {
                if (key === 'configuration') {
                    const configuration = this.getConfiguration(dashboard, key);
                    copy.configuration = configuration;
                } else {
                    copy[key] = angular.copy(dashboard[key]);
                }
            }
        }
        delete (copy.guid);
        delete (copy.data);
        return copy;
    }

    private getConfiguration(dashboard: any, key: string): DashboardConfiguration {
        const configuration = {} as DashboardConfiguration;
        for (const k in dashboard[key]) {
            if (k !== 'widgets') {
                configuration[k] = angular.copy(dashboard.configuration[k]);
            }
        }
        return configuration;
    }

    private map(widget: any, newName: string, data: MappingResult, series: ISeriesConfiguration[], allowRawValues?: boolean): WidgetWithMappings {
        const copy = Object.assign(this._widgetFactory.createWidget(widget.type), JSON.parse(JSON.stringify(this.getCopy(widget, newName))));
        const mappings = data;
        if (series) {
            const newSeries = this.getNewSeries(series, data, allowRawValues);
            try {
                (copy as IdataWidget).setSeries(newSeries);
            }
            catch (e) {
                console.error(`Can't set newly renamed series as setSeries method is missing on widget '${copy.name}' of type '${widget.type}'`);
                console.error(e);
            }
        }
        return { copy, mappings };
    }

    private getNewSeries(series: ISeriesConfiguration[], data: MappingResult, allowRawValues: boolean): ISeriesConfiguration[] {
        return series.map(s => {
            const newSeries = angular.copy(s);
            if (data.mapping[newSeries.guid]) {
                const vm = data.mapping[newSeries.guid];
                newSeries.guid = vm.guid;
                const values = this._seriesSelectorService.getValues(vm);
                if (!values.length) {
                    //group states
                } else {
                    if (allowRawValues) {
                        values.push(ValueTypes.raw);
                    }
                    if (!_.some(values, v => v.key === newSeries.value || v.key === newSeries.valueType)) {
                        newSeries.value = values[0].key;
                    }
                }
            }
            return newSeries;
        });
    }

    /**Deep clone of a widget, removing adequate properties. */
    private getCopy(widget: IWidget, newName?: string): any {
        if (!newName) {
            newName = widget.name;
        }
        const copy: any = {};
        for (const key in widget) {
            if (key.substr(0, 1) !== '$' && key.substr(0, 1) !== '_') {
                copy[key] = angular.copy(widget[key]);
            }
        }

        if (copy.configuration?.shapes) {
            copy.configuration.shapes.forEach(s => {
                s.guid = Helper.getGuid();
                s.points.forEach(p => {
                    p.guid = Helper.getGuid();
                });
            });
        }

        delete (copy.guid);
        delete (copy.data);
        copy.name = newName;
        return copy;
    }

    private remapSeries(seriesGuids: string[], vms: IValueViewModel[], groups: Group[], seriesRename: StringReplacement[], groupsRename: StringReplacement[]): MappingResult {
        try {
            const mapping: { [originalSeriesGuid: string]: IValueViewModel } = {};
            const vmIndex = {};
            vms.forEach(vm => vmIndex[vm.guid] = vm);
            const groupIndex = {};
            groups.forEach(g => groupIndex[g.guid] = g);

            // replace tag/identifier series
            const valueVM = seriesGuids.map(s => vmIndex[s]).filter(vm => vm);

            const result = helpers.renameViewModels(valueVM, vms, seriesRename, groupsRename);
            result.replaced.forEach(r => mapping[r.originalVm.guid] = r.newVm);

            // replace group series
            const groupsVM = seriesGuids.map(s => groupIndex[s]).filter(vm => vm);
            const groupResult = helpers.renameGroups(groupsVM, groups, groupsRename);
            groupResult.replaced.forEach(r => mapping[r.originalVm.guid] = r.newVm);
            const notFound = result.notFound.concat(groupResult.notFound);

            return { irreplacible: notFound, mapping: mapping };
        } catch (e) {
            this._loggerService.logError(e);
            throw e;
        }
    }

    getSeries(widget: IWidget): ISeriesConfiguration[] {
        let series: ISeriesConfiguration[];
        const dw = (widget as IdataWidget);
        if (dw?.getSeries) {
            series = dw.getSeries();
        } else {
            if (widget) {
                series = (widget.configuration as any).series;
            }
        }
        return series;
    }
}